/**
 * Permissões do portal do anunciante (alinhado a `advertiserUserPermissions.js` no server-eurobem).
 *
 * owner  — Proprietário: tudo
 * admin  — Administrador: usuários, ofertas, resgates, validar
 * manager — Gerente: ofertas, resgates, validar (sem usuários)
 * user   — Atendente: só validar
 */

export const ADVERTISER_ROLE_LABELS = {
    owner: 'Proprietário',
    admin: 'Administrador',
    manager: 'Gerente',
    user: 'Atendente',
};

/** Textos de ajuda para a tela de usuários/convites. */
export const ADVERTISER_ROLE_GUIDES = [
    {
        role: 'owner',
        label: 'Proprietário',
        description:
            'Acesso total: usuários, ofertas, resgates, validação, desfazer validação e dados da empresa.',
    },
    {
        role: 'admin',
        label: 'Administrador',
        description:
            'Convida e gerencia usuários, cadastra ofertas, consulta resgates, valida códigos e pode desfazer validações.',
    },
    {
        role: 'manager',
        label: 'Gerente',
        description:
            'Cadastra ofertas, consulta resgates, valida códigos e pode desfazer validações. Não gerencia usuários.',
    },
    {
        role: 'user',
        label: 'Atendente',
        description:
            'Apenas valida códigos de resgate no ponto de venda. Não consulta a lista nem desfaz validações.',
    },
];

export const ADVERTISER_ROLES_THAT_MANAGE_USERS = ['owner', 'admin'];
export const ADVERTISER_ROLES_THAT_MANAGE_OFFERS = ['owner', 'admin', 'manager'];
export const ADVERTISER_ROLES_THAT_VIEW_RESCUES = ['owner', 'admin', 'manager'];
/** Gerente+ podem desfazer validação. Atendente não. */
export const ADVERTISER_ROLES_THAT_CANCEL_VALIDATION = ['owner', 'admin', 'manager'];
export const ADVERTISER_ROLES_THAT_VALIDATE = ['owner', 'admin', 'manager', 'user'];
export const ADVERTISER_ROLES_THAT_VIEW_DASHBOARD = ['owner', 'admin', 'manager'];
export const ADVERTISER_ROLES_THAT_MANAGE_COMPANY = ['owner', 'admin', 'manager'];

export function getAdvertiserRole(user) {
    return user?.role ?? user?.advertiserRole ?? null;
}

function roleIn(user, allowed) {
    const role = getAdvertiserRole(user);
    return Boolean(role && allowed.includes(role));
}

export function canManageAdvertiserUsers(user) {
    return roleIn(user, ADVERTISER_ROLES_THAT_MANAGE_USERS);
}

export function canManageAdvertiserOffers(user) {
    return roleIn(user, ADVERTISER_ROLES_THAT_MANAGE_OFFERS);
}

export function canViewAdvertiserRescues(user) {
    return roleIn(user, ADVERTISER_ROLES_THAT_VIEW_RESCUES);
}

export function canCancelAdvertiserValidation(user) {
    return roleIn(user, ADVERTISER_ROLES_THAT_CANCEL_VALIDATION);
}

export function canValidateAdvertiserRescues(user) {
    return roleIn(user, ADVERTISER_ROLES_THAT_VALIDATE);
}

export function canViewAdvertiserDashboard(user) {
    return roleIn(user, ADVERTISER_ROLES_THAT_VIEW_DASHBOARD);
}

export function canManageAdvertiserCompany(user) {
    return roleIn(user, ADVERTISER_ROLES_THAT_MANAGE_COMPANY);
}

/** Atendente (role `user`): só validação de código. */
export function isAdvertiserAttendant(user) {
    return getAdvertiserRole(user) === 'user';
}

export function getAdvertiserHomePath(user) {
    if (isAdvertiserAttendant(user)) return '/anunciante/validar';
    return '/anunciante';
}

/**
 * Rotas do shell autenticado permitidas por cargo.
 * Paths sem match caem no home do cargo.
 */
export function canAccessAdvertiserPath(user, pathname) {
    const path = String(pathname || '');

    if (path === '/anunciante' || path === '/anunciante/') {
        return canViewAdvertiserDashboard(user);
    }
    if (path.startsWith('/anunciante/ofertas')) {
        return canManageAdvertiserOffers(user);
    }
    if (path.startsWith('/anunciante/usuarios')) {
        return canManageAdvertiserUsers(user);
    }
    if (path.startsWith('/anunciante/resgates')) {
        return canViewAdvertiserRescues(user);
    }
    if (path.startsWith('/anunciante/validar')) {
        return canValidateAdvertiserRescues(user);
    }
    if (path.startsWith('/anunciante/dados') || path.startsWith('/anunciante/perfil')) {
        return canManageAdvertiserCompany(user);
    }
    return !isAdvertiserAttendant(user);
}
